import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import axiosInstance from "../HelperAxios/axios";
import useNotificationSocket from "../hooks/useNotificationSocket";
import { playNotificationSound } from "../Utils/sound";

const NotificationContext = createContext({
  unreadCount: 0,
  markAllRead: () => {},
});

export const NotificationProvider = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const isMounted = useRef(true);

  // Fetch initial unread badge count
  useEffect(() => {
    isMounted.current = true;
    axiosInstance
      .get("/message/unseenCount")
      .then((res) => {
        if (isMounted.current) {
          setUnreadCount(res.data?.count ?? 0);
        }
      })
      .catch(() => {});

    return () => {
      isMounted.current = false;
    };
  }, []);

  // Handle real-time incoming notification
  const handleCreated = useCallback((notification) => {
    setUnreadCount((prev) => prev + 1);

    const subject = notification?.subject || "New notification";
    toast.info(subject, {
      position: "top-right",
      autoClose: 5000,
    });

    playNotificationSound();
  }, []);

  useNotificationSocket({ onCreated: handleCreated });

  // Mark all notifications as read and reset badge
  const markAllRead = useCallback(() => {
    axiosInstance.post("/message/seen").catch(() => {});
    setUnreadCount(0);
  }, []);

  return (
    <NotificationContext.Provider value={{ unreadCount, markAllRead }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);

NotificationProvider.propTypes = {
  children: PropTypes.node,
};

export default NotificationContext;
